import type { AdminCategorySelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { TooltipComponent } from '@components/core/tooltip.component';
import { NumberBadge } from '@components/shared/number-badge.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { withForm } from '@components/ui/form/form';
import { FormDescription } from '@components/ui/form/shared.component';
import { useAdminAvailableSubcategoriesQuery } from '@queries/admin-categories.query';
import { useStore } from '@tanstack/react-form';
import { mapToSelectOptions } from '@utils/select.util';
import { CheckIcon, EditIcon, XIcon } from 'lucide-react';
import { parseAsString, useQueryState } from 'nuqs';
import { useMemo } from 'react';
import { adminCategoryFormOptions } from './admin-category-form-options';

type OptionSource = {
  archived?: boolean;
  id: string;
  name: string;
};

export const AdminCategorySubcategories = withForm({
  ...adminCategoryFormOptions,
  props: {
    category: undefined as unknown as AdminCategorySelectDto,
    disableEdit: false,
    isBusy: false,
    isEditing: false,
    onDiscard: undefined as unknown as () => void,
    onEdit: undefined as unknown as () => void,
    onSave: undefined as unknown as () => void,
  },
  render: function Render({ category, disableEdit, form, isBusy, isEditing, onDiscard, onEdit, onSave }) {
    const [, setSelectedCategoryId] = useQueryState('categoryId', parseAsString);

    const { data: availableSubcategoriesData, isPending: isLoadingOptions } = useAdminAvailableSubcategoriesQuery();

    const optionSources = useMemo<OptionSource[]>(() => {
      const map = new Map<string, OptionSource>();

      for (const subcategory of category.subcategories ?? []) {
        map.set(subcategory.id, {
          archived: subcategory.archived,
          id: subcategory.id,
          name: subcategory.name,
        });
      }

      for (const candidate of availableSubcategoriesData?.categories ?? []) {
        if (candidate.id === category.id || map.has(candidate.id)) {
          continue;
        }
        map.set(candidate.id, {
          archived: candidate.archived,
          id: candidate.id,
          name: candidate.name,
        });
      }

      return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [availableSubcategoriesData?.categories, category.id, category.subcategories]);

    const subcategoryOptions = mapToSelectOptions(optionSources);

    const totalDependents =
      category.subcategories?.reduce((sum, subcategory) => sum + (subcategory.totalDependencies ?? 0), 0) ?? 0;

    const currentSubcategories = useStore(form.store, (state) => state.values.subcategories ?? []);

    const parentCategoryId = useStore(form.store, (state) => state.values.parentCategoryId);
    const canEdit = !parentCategoryId;

    return (
      <Tile
        className="border border-gray-200 shadow-none"
        header={{
          actions: isEditing ? (
            <div className="flex gap-1">
              <IconButton
                disabled={isBusy}
                onClick={onDiscard}
                size="md"
                icon={<XIcon size={16} />}
                variant="secondary"
              />

              <IconButton
                disabled={isBusy}
                onClick={onSave}
                size="md"
                icon={<CheckIcon size={16} />}
                variant="primary"
              />
            </div>
          ) : undefined,
          iconButton: isEditing
            ? undefined
            : {
                disabled: disableEdit,
                disabledTooltip: 'Save or discard changes before editing',
                icon: <EditIcon size={16} />,
                onClick: onEdit,
              },
          title: (
            <div className="flex items-center gap-2">
              <NumberBadge
                size="md"
                value={currentSubcategories.length}
              />
              <div>
                <span className="text-sm font-semibold text-gray-900">Subcategories</span>
                <div className="flex items-center gap-1">
                  <TooltipComponent
                    side="top"
                    trigger={
                      <span className="text-xs font-normal border-dashed border-b text-gray-500">
                        Used by {totalDependents === 1 ? '1 entity' : `${totalDependents} entities`}
                      </span>
                    }
                  >
                    Entities (technologies, mission assessments, etc.) that are using these subcategories.
                  </TooltipComponent>
                </div>
              </div>
            </div>
          ),
        }}
        hoverReveal={!isEditing && !disableEdit}
      >
        {(isEditing || (category.subcategories?.length ?? 0) > 0) && (
          <div className="flex flex-col gap-3">
            {isEditing ? (
              <div className="flex flex-col gap-3">
                <form.AppForm>
                  <form.AppField name="subcategories">
                    {(field) => (
                      <>
                        <field.SelectMultipleField
                          disabled={!canEdit || isLoadingOptions}
                          options={subcategoryOptions}
                          placeholder={
                            canEdit ? 'Select subcategories...' : 'Remove the parent category to manage subcategories.'
                          }
                        />
                        <FormDescription>
                          The subcategory options are limited to categories that are not archived, and do not have any
                          parent category or subcategory.
                        </FormDescription>
                      </>
                    )}
                  </form.AppField>
                </form.AppForm>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {category.subcategories?.map((subcategory) => (
                  <button
                    key={subcategory.id}
                    className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors hover:bg-gray-50"
                    onClick={() => void setSelectedCategoryId(subcategory.id)}
                    type="button"
                  >
                    <span className="text-left font-medium">{subcategory.name}</span>
                    {subcategory.archived && (
                      <Badge
                        size="xs"
                        variant="secondary"
                      >
                        Archived
                      </Badge>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </Tile>
    );
  },
});
